import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, PackageSearch, ScanBarcode, Undo2 } from "lucide-react";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { apiFetch } from "../api/apiFetch";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FlowLoader from "../components/FlowLoader";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import FitWord from "../components/FitWord";
import "../styles/scansheet.css";
import "../styles/picking.css";
import "../styles/dispatch-unload.css";
import "../styles/dispatch-found-items.css";

// ---------------------------------------------------------------------------
// Dispatch › Found items (Alex, 2026-10-20) — built for Poland, where every
// SKU is a single unique unit. ATL and above.
//
// The leader scans a stray item (POST /dispatch/found/scan):
//   • an open order still wants it → scan a found-items shelf next to
//     dispatch (POST /dispatch/found/shelve). The packer collects it from
//     there exactly like a Pembroke Flagged Pick — the pack desk shows the
//     "collect from shelf" notice and ships the order with it.
//   • …but a packer has that order open on a desk right now → amber "Take
//     it to the desk" card naming desk and packer (JEU718798, 2026-10-22:
//     shelved behind the packer's back, it shipped without it). "Nobody
//     there" goes back to the shelf scan with the desk check skipped.
//   • already on a shelf for that order → amber card naming the shelf.
//   • the order shipped without it and today is no later than the next
//     working day after it shipped (nothing refunded) → amber "Ship it on
//     its own" card with the deadline (Alex, 2026-10-20). Past that the
//     customer may have been refunded, so it is booked back in instead.
//   • shipped past the deadline / cancelled / refunded / never ordered →
//     red "Book item back in" card, so it goes back up for auction
//     through Goods In.
//
// Under the scan: what is on the shelves waiting for a packer.
//
// Book in pill (Alex, 2026-10-20): units whose order went away while they
// sat on the shelf — cancelled, or the packer shipped without it (the desk
// marked it missing, so it was never collected). The leader scans each one
// as it comes off the shelf (its row turns green), then presses "Taken off
// shelf" once for every green row; they go back up through Goods In. A unit
// that is not on the shelf can't be scanned, so tapping a row selects it too.
// ---------------------------------------------------------------------------

type Mode = "found" | "book_in";

type OrderInfo = {
    orderId: string;
    orderNumber: string;
    status: string;
    customer: string | null;
    packedAt: string | null;
    // Shipped orders: the moment (ISO) after which the unit may no longer be
    // sent on its own — 48 hours after packing (Alex, 2026-10-23).
    resendBy?: string | null;
    // Units of this SKU whose refund is decided but not yet issued (Orders ›
    // Pending Refund). Shipping the found unit cancels one.
    refundPending?: number;
    shelves: string[];
};

// The pack desk that has the order open right now (BE loadDeskSession).
type DeskInfo = { deskId: string | null; packer: string | null };

// force: the leader went to the desk and nobody was packing the order, so
// the shelf scan skips the open-at-a-desk check.
type Found = { sku: string; title: string | null; order: OrderInfo; force?: boolean };

type BookBackReason = "dispatched" | "cancelled" | "refunded" | "no_order" | "not_worth_shipping";

// Sending the unit on its own vs what the customer paid for it, in the
// order's currency (BE services/found-item-cost.js, Alex 2026-10-23).
// source 'tariff' = the courier's recorded tariff; 'estimate' = the flat
// per-country figure used until the tariff is in the Portal.
export type CostCheck = { worthIt: boolean; source: "tariff" | "estimate"; currency: string; shipping: number; value: number };

type Card =
    | { kind: "book_back_in"; sku: string; title: string | null; reason: BookBackReason; order: OrderInfo | null; costCheck?: CostCheck | null; refundDone?: boolean }
    | { kind: "already_shelved"; sku: string; order: OrderInfo }
    | { kind: "ship_separately"; sku: string; title: string | null; order: OrderInfo; costCheck?: CostCheck | null }
    // The order's only unit (Claire, Newport JUK728212, 2026-10-27): no tote
    // brings the order to a desk, so it goes through Send to packing instead
    // of the shelf. Written steps only — the leader is on a handheld.
    | { kind: "single_item"; sku: string; title: string | null; order: OrderInfo }
    | { kind: "flagged"; order: OrderInfo; shelf: string }
    | { kind: "at_desk"; sku: string; title: string | null; order: OrderInfo; desk: DeskInfo };

export type FoundListItem = {
    id: number;
    orderId: string;
    orderNumber: string;
    orderStatus: string | null;
    sku: string;
    title: string | null;
    qty: number;
    status: "staged" | "put_back";
    shelf: string;
    stagedAt: string | null;
    stagedBy: string | null;
    barcodes?: string[];
    cancelReason?: string | null;
};

// Which put-back row a Book in scan is: the SKU itself (Poland SKUs may not
// be in products) or one of its barcodes, skipping rows already scanned.
export const matchBookIn = (list: FoundListItem[], scanned: Set<number>, value: string):
    { item: FoundListItem | null; already: boolean } => {
    const v = value.trim();
    const hits = list.filter((x) => x.sku === v || (x.barcodes ?? []).includes(v));
    const item = hits.find((x) => !scanned.has(x.id)) ?? null;
    return { item, already: !item && hits.length > 0 };
};

// resend_by is a timestamp (packed + 48 h): day and time, local.
const fmtWhen = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleString([], { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};
export const fmtMoney = (amount: number, currency: string): string => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
};

const DispatchFoundItems = () => {
    usePageTitle("dispatch_found_items");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [scan, setScan] = useState("");
    const [loading, setLoading] = useState(false);
    const [found, setFound] = useState<Found | null>(null);
    const [card, setCard] = useState<Card | null>(null);
    const [last, setLast] = useState<{ orderNumber: string; shelf: string } | null>(null);
    const [items, setItems] = useState<FoundListItem[]>([]);
    const [mode, setMode] = useState<Mode>("found");
    const [scannedIds, setScannedIds] = useState<Set<number>>(() => new Set());
    const [bookInBusy, setBookInBusy] = useState(false);
    const [bookInDone, setBookInDone] = useState<number | null>(null);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const flash = useCallback((status: "success" | "error", message?: string) => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        setErrorMessage(message);
        statusTimerRef.current = setTimeout(() => { setScanStatus("idle"); setErrorMessage(undefined); }, status === "error" ? 900 : 500);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const res = await apiFetch(`/dispatch/found/list`, {});
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) return;
            const data = await res.json();
            const next: FoundListItem[] = Array.isArray(data.items) ? data.items : [];
            setItems(next);
            // A unit cleared elsewhere drops out of the scanned set too.
            setScannedIds((prev) => {
                const live = new Set(next.filter((x) => x.status === "put_back").map((x) => x.id));
                const kept = new Set(Array.from(prev).filter((id) => live.has(id)));
                return kept.size === prev.size ? prev : kept;
            });
        } catch {
            /* the list is a courtesy; the scans are authoritative */
        }
    }, [navigate]);

    useEffect(() => { refresh(); }, [refresh]);

    const post = useCallback(async (path: string, body: object) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return null; }
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
    }, [navigate]);

    const scanItem = useCallback(async (barcode: string) => {
        setLoading(true);
        try {
            const data = await post(`/dispatch/found/scan`, { barcode });
            if (!data) return;
            if (data.outcome === "order" && data.order) {
                playSuccess();
                flash("success");
                setFound({ sku: data.sku, title: data.title ?? null, order: data.order });
                return;
            }
            if (data.outcome === "at_desk" && data.order) {
                playSuccess();
                flash("success");
                setCard({ kind: "at_desk", sku: data.sku, title: data.title ?? null, order: data.order, desk: data.desk ?? { deskId: null, packer: null } });
                return;
            }
            playError();
            flash("error");
            if (data.outcome === "already_shelved" && data.order) {
                setCard({ kind: "already_shelved", sku: data.sku, order: data.order });
                return;
            }
            if (data.outcome === "ship_separately" && data.order) {
                setCard({ kind: "ship_separately", sku: data.sku, title: data.title ?? null, order: data.order, costCheck: data.costCheck ?? null });
                return;
            }
            if (data.outcome === "single_item" && data.order) {
                setCard({ kind: "single_item", sku: data.sku, title: data.title ?? null, order: data.order });
                return;
            }
            setCard({
                kind: "book_back_in", sku: String(data.sku ?? barcode), title: data.title ?? null,
                reason: (data.reason ?? "no_order") as BookBackReason, order: data.order ?? null, costCheck: data.costCheck ?? null,
            });
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [post, flash, t]);

    const shelveItem = useCallback(async (location: string) => {
        if (!found) return;
        setLoading(true);
        try {
            const data = await post(`/dispatch/found/shelve`, {
                orderId: found.order.orderId, sku: found.sku, location, ...(found.force ? { force: true } : {}),
            });
            if (!data) return;
            if (data.valid) {
                playSuccess();
                flash("success");
                const shelf = String(data.shelf?.label ?? location);
                setLast({ orderNumber: found.order.orderNumber, shelf });
                if (data.orderFlagged) setCard({ kind: "flagged", order: found.order, shelf });
                setFound(null);
                refresh();
                return;
            }
            playError();
            switch (data.reason) {
                case "unknown_location":
                case "not_staging":
                    flash("error", t("found_not_a_shelf"));
                    return;
                case "wrong_warehouse":
                    flash("error", t("found_wrong_warehouse"));
                    return;
                case "at_desk":
                    flash("error");
                    setCard({
                        kind: "at_desk", sku: found.sku, title: found.title, order: data.order ?? found.order,
                        desk: data.desk ?? { deskId: null, packer: null },
                    });
                    setFound(null);
                    return;
                case "already_shelved":
                    flash("error");
                    setCard({ kind: "already_shelved", sku: found.sku, order: data.order ?? found.order });
                    setFound(null);
                    return;
                case "ship_separately":
                    flash("error");
                    setCard({ kind: "ship_separately", sku: found.sku, title: found.title, order: data.order ?? found.order, costCheck: data.costCheck ?? null });
                    setFound(null);
                    return;
                case "single_item":
                    flash("error");
                    setCard({ kind: "single_item", sku: found.sku, title: found.title, order: data.order ?? found.order });
                    setFound(null);
                    return;
                case "book_back_in":
                case "order_not_found":
                    flash("error");
                    setCard({
                        kind: "book_back_in", sku: found.sku, title: found.title,
                        reason: (data.bookBackInReason ?? "no_order") as BookBackReason, order: data.order ?? found.order,
                        costCheck: data.costCheck ?? null,
                    });
                    setFound(null);
                    return;
                default:
                    flash("error", t("scan_failed"));
            }
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [found, post, flash, refresh, t]);

    const putBack = useMemo(() => items.filter((x) => x.status === "put_back"), [items]);

    const bookInScan = useCallback((value: string) => {
        const { item, already } = matchBookIn(putBack, scannedIds, value);
        if (!item) {
            playError();
            flash("error", already ? t("found_book_in_already") : t("found_book_in_not_listed"));
            return;
        }
        playSuccess();
        flash("success");
        setBookInDone(null);
        setScannedIds((prev) => new Set(prev).add(item.id));
    }, [putBack, scannedIds, flash, t]);

    // Tapping a row selects it without a scan — the unit is not on the shelf
    // (gone missing, label unreadable), but the row still has to come off
    // the list. Tapping again deselects it.
    const toggleBookIn = useCallback((id: number) => {
        setBookInDone(null);
        setScannedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleSubmit = useCallback((scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        setScan("");
        if (!val || loading) return;
        if (card) setCard(null);
        if (mode === "book_in") bookInScan(val);
        else if (found) shelveItem(val);
        else scanItem(val);
    }, [scan, loading, card, mode, found, bookInScan, shelveItem, scanItem]);

    // One press for every scanned (green) row.
    const takeOffShelf = useCallback(async () => {
        const taskIds = Array.from(scannedIds);
        if (taskIds.length === 0) return;
        setBookInBusy(true);
        try {
            const data = await post(`/dispatch/found/booked-back`, { taskIds });
            if (!data) return;
            const done = new Set<number>(Array.isArray(data.done) ? data.done : []);
            setItems((list) => list.filter((x) => !done.has(x.id)));
            setScannedIds(new Set());
            setBookInDone(done.size);
            if (done.size > 0) playSuccess();
            if (!data.valid || (Array.isArray(data.skipped) && data.skipped.length > 0)) refresh();
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setBookInBusy(false);
        }
    }, [scannedIds, post, refresh, flash, t]);

    // Shipping it alone costs more than the item: one confirm puts the unit
    // on Pending Refund; the leader then books it in through Goods In.
    const [refundBusy, setRefundBusy] = useState(false);
    const refundInstead = useCallback(async (c: Extract<Card, { kind: "book_back_in" }>) => {
        if (!c.order) return;
        setRefundBusy(true);
        try {
            const data = await post(`/dispatch/found/refund-instead`, { orderId: c.order.orderId, sku: c.sku });
            if (!data) return;
            if (data.valid) {
                playSuccess();
                setCard({ ...c, refundDone: true });
                return;
            }
            playError();
            setCard(null);
            flash("error", t("found_refund_instead_refused"));
        } catch {
            playError();
            flash("error", t("server_error"));
        } finally {
            setRefundBusy(false);
        }
    }, [post, flash, t]);

    const costLine = (c: CostCheck) => t(c.source === "tariff" ? "found_cost_line_tariff" : "found_cost_line_estimate", {
        shipping: fmtMoney(c.shipping, c.currency), value: fmtMoney(c.value, c.currency),
    });

    const switchMode = (next: Mode) => {
        if (next === mode) return;
        setMode(next);
        setFound(null);
        setCard(null);
        setBookInDone(null);
        if (next === "book_in") refresh();
    };

    const bookBackText = (c: Extract<Card, { kind: "book_back_in" }>) => {
        const order = c.order?.orderNumber ?? "";
        switch (c.reason) {
            case "dispatched": return t("found_book_back_dispatched", { order, time: fmtDate(c.order?.packedAt) });
            case "cancelled": return t("found_book_back_cancelled", { order });
            case "refunded": return t("found_book_back_refunded", { order });
            case "not_worth_shipping": return t(c.refundDone ? "found_book_back_not_worth_done" : "found_book_back_not_worth", { order });
            default: return t("found_book_back_no_order", { sku: c.sku });
        }
    };

    const command = found ? t("found_scan_shelf") : t("found_scan_item");
    const scannedCount = putBack.filter((x) => scannedIds.has(x.id)).length;

    return (
        <div
            className="scan-panel unload-panel found-panel"
            style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}
        >
            <ScanFlashOverlay active={scanStatus === "error"} message={errorMessage} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("dispatch_found_items")}</h1>
                {/* Found / Book in pills — same icon toggle as Move Stock's
                    Move / Consolidate, hidden while a found unit is in hand
                    (its shelf scan is still to come). */}
                {!found && (
                    <div className="picking-tier-toggle" role="radiogroup" aria-label={t("dispatch_found_items")}>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "found"}
                            className={`picking-tier-pill${mode === "found" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => switchMode("found")}
                            title={t("found_pill_found")}
                            aria-label={t("found_pill_found")}
                            data-testid="found-pill-found"
                        >
                            <PackageSearch size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "book_in"}
                            className={`picking-tier-pill found-pill--book-in${mode === "book_in" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => switchMode("book_in")}
                            title={t("found_pill_book_in")}
                            aria-label={t("found_pill_book_in")}
                            data-testid="found-pill-book-in"
                        >
                            <Undo2 size={16} strokeWidth={2.2} aria-hidden="true" />
                            {putBack.length > 0 && <span className="found-pill-badge" data-testid="found-book-in-badge">{putBack.length}</span>}
                        </button>
                    </div>
                )}
                <button className="page-back-btn" onClick={() => navigate("/dispatch/menu")} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {mode === "found" && (<>
            <div className="scan-centre">
                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                {loading && <FlowLoader />}
                {!loading && (
                    <div className="unload-target" data-testid={found ? "found-shelf-step" : "found-item-step"}>
                        {found && (
                            <div className="found-order" data-testid="found-order">
                                <span className="found-order-label">{t("found_for_order")}</span>
                                <span className="found-order-number">{found.order.orderNumber}</span>
                                <span className="found-order-meta">
                                    {found.title ?? found.sku}
                                    {found.order.customer ? ` · ${found.order.customer}` : ""}
                                </span>
                            </div>
                        )}
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {command.toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        {!found && last && (
                            <span className="unload-target-hint">
                                <ScanBarcode size={16} />
                                {t("found_last_booked", { order: last.orderNumber, shelf: last.shelf })}
                            </span>
                        )}
                        {found && (
                            <button type="button" className="btn btn-ghost btn-sm unload-aside-btn" onClick={() => setFound(null)}>
                                {t("cancel")}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {card && (
                <div
                    className={`unload-card-overlay unload-card-overlay--${card.kind === "book_back_in" ? "stop" : "warn"}`}
                    role="alertdialog"
                    data-testid={`found-card-${card.kind}`}
                >
                    <div className="unload-card">
                        <h2 className="unload-card-title">
                            {card.kind === "book_back_in" && t("found_book_back_title")}
                            {card.kind === "already_shelved" && t("found_already_shelved_title")}
                            {card.kind === "flagged" && t("found_flagged_title")}
                            {card.kind === "ship_separately" && t("found_ship_separately_title")}
                            {card.kind === "single_item" && t("found_single_item_title")}
                            {card.kind === "at_desk" && t("found_at_desk_title", { desk: card.desk.deskId ?? "—" })}
                        </h2>
                        <p className="unload-card-text">
                            {card.kind === "book_back_in" && bookBackText(card)}
                            {card.kind === "already_shelved" && t("found_already_shelved_detail", {
                                order: card.order.orderNumber, shelf: card.order.shelves.join(", ") || "—",
                            })}
                            {card.kind === "flagged" && t("found_flagged_detail", { order: card.order.orderNumber, shelf: card.shelf })}
                            {card.kind === "ship_separately" && t(card.order.packedAt ? "found_ship_separately_detail" : "found_ship_separately_detail_unpacked", {
                                order: card.order.orderNumber,
                                customer: card.order.customer ?? "—",
                                shipped: fmtDate(card.order.packedAt),
                            })}
                            {card.kind === "at_desk" && t(card.desk.packer ? "found_at_desk_detail" : "found_at_desk_detail_no_packer", {
                                order: card.order.orderNumber, desk: card.desk.deskId ?? "—", packer: card.desk.packer ?? "",
                            })}
                            {card.kind === "single_item" && t("found_single_item_detail", { order: card.order.orderNumber })}
                        </p>
                        {card.kind === "single_item" && (
                            <p className="unload-card-sub found-how" data-testid="found-how">
                                {t("found_single_item_how", { order: card.order.orderNumber })}
                            </p>
                        )}
                        {card.kind === "ship_separately" && (
                            <p className="unload-card-sub found-how" data-testid="found-how">
                                {t("found_ship_how", { order: card.order.orderNumber })}
                                {(card.order.refundPending ?? 0) > 0 && ` ${t("found_ship_how_refund_pending")}`}
                            </p>
                        )}
                        {card.kind === "ship_separately" && card.order.resendBy && (
                            <p className="unload-card-sub found-deadline" data-testid="found-deadline">
                                {t("found_ship_separately_deadline", { time: fmtWhen(card.order.resendBy) })}
                            </p>
                        )}
                        {(card.kind === "book_back_in" || card.kind === "ship_separately") && card.costCheck && (
                            <p className="unload-card-sub found-cost" data-testid="found-cost">{costLine(card.costCheck)}</p>
                        )}
                        {(card.kind === "book_back_in" || card.kind === "ship_separately" || card.kind === "at_desk" || card.kind === "single_item") && <p className="unload-card-sub">{card.title ? `${card.title} · ${card.sku}` : card.sku}</p>}
                        <div className="unload-card-actions">
                            {card.kind === "book_back_in" && card.reason === "not_worth_shipping" && !card.refundDone ? (<>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    data-testid="found-refund-instead"
                                    disabled={refundBusy}
                                    onClick={() => refundInstead(card)}
                                >
                                    {t("found_refund_instead")}
                                </button>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCard(null)}>
                                    {t("cancel")}
                                </button>
                            </>) : (
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCard(null)}>
                                    {t("unload_ok")}
                                </button>
                            )}
                            {card.kind === "at_desk" && (
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    data-testid="found-at-desk-shelve"
                                    onClick={() => {
                                        setFound({ sku: card.sku, title: card.title, order: card.order, force: true });
                                        setCard(null);
                                    }}
                                >
                                    {t("found_at_desk_shelve")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* No "on the shelf" list here (Alex, 2026-10-21): it grew a row
                per booked item behind the scan prompt. What is on the shelf
                lives on Dispatch › Flagged › Found items on shelf. */}
            </>)}

            {mode === "book_in" && (
                <div className="found-book-in" data-testid="found-book-in">
                    <div className="found-book-in-head">
                        <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                        <p className="dispatch-stats-title">{t("found_book_in_title")}</p>
                        {putBack.length > 0 && <p className="found-book-in-hint">{t("found_book_in_hint")}</p>}
                    </div>
                    {bookInDone !== null && bookInDone > 0 && (
                        <p className="found-book-in-done" data-testid="found-book-in-done">
                            <Check size={14} /> {t("found_taken_off_done", { count: String(bookInDone) })}
                        </p>
                    )}
                    {putBack.length === 0 ? (
                        <p className="load-waiting-empty">{t("found_book_in_none")}</p>
                    ) : (<>
                        <ul className="found-book-in-list">
                            {putBack.map((x) => {
                                const done = scannedIds.has(x.id);
                                return (
                                    <li
                                        key={x.id}
                                        className={`found-book-in-item${done ? " found-book-in-item--scanned" : ""}`}
                                        data-testid="found-book-in-item"
                                        data-scanned={done ? "1" : "0"}
                                        role="checkbox"
                                        aria-checked={done}
                                        onClick={() => toggleBookIn(x.id)}
                                    >
                                        <span className="found-book-in-icon">{done ? <Check size={16} /> : <ScanBarcode size={16} />}</span>
                                        <span className="found-book-in-main">
                                            <span className="found-book-in-label">{x.title ?? x.sku}</span>
                                            <span className="found-book-in-meta">
                                                {x.sku} · {x.cancelReason === "not_at_desk"
                                                    ? t("found_book_in_reason_not_at_desk", { order: x.orderNumber })
                                                    : t("found_book_in_reason_cancelled", { order: x.orderNumber })}
                                            </span>
                                        </span>
                                        <span className="found-book-in-shelf">{x.shelf}</span>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="found-book-in-actions">
                            <span className="found-book-in-count">{t("found_book_in_scanned", { scanned: String(scannedCount), total: String(putBack.length) })}</span>
                            <button
                                type="button"
                                className="btn btn-primary dispatch-modal-btn"
                                onClick={takeOffShelf}
                                disabled={scannedCount === 0 || bookInBusy}
                                data-testid="found-take-off"
                            >
                                <Undo2 size={14} /> {t("found_taken_off_shelf", { count: String(scannedCount) })}
                            </button>
                        </div>
                    </>)}
                </div>
            )}

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading}
            />
        </div>
    );
};

export default DispatchFoundItems;
